import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Search, X, Book, Play, BookOpen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { searchSite, type SearchResult } from '@/services/searchService'
import { getLocalizedValue } from '@sakeena/shared'
import { getLangFromPathname, localizePath } from '@/utils/localeFromUrl'

interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
  initialSearchQuery?: string
}

export default function SearchModal({ isOpen, onClose, initialSearchQuery = '' }: SearchModalProps) {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery)
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language || 'ar'
  const navigate = useNavigate()
  const location = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)

  // Update search query when initialSearchQuery changes or modal opens
  useEffect(() => {
    if (isOpen && initialSearchQuery) {
      setSearchQuery(initialSearchQuery)
    } else if (!isOpen) {
      // Clear search when modal closes
      setSearchQuery('')
    }
  }, [isOpen, initialSearchQuery])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (searchQuery.length >= 2) {
      setIsLoading(true)
      // Debounce search
      const timer = setTimeout(async () => {
        try {
          const searchResults = await searchSite(searchQuery)
          setResults(searchResults.slice(0, 10)) // Limit to 10 results
        } catch (error) {
          console.error('Search error:', error)
          setResults([])
        } finally {
          setIsLoading(false)
        }
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setResults([])
      setIsLoading(false)
    }
  }, [searchQuery])

  const handleResultClick = (result: SearchResult) => {
    const lang = getLangFromPathname(location.pathname)
    navigate(localizePath(lang, result.url))
    onClose()
  }

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'book':
      case 'library':
        return <Book className="w-4 h-4" />
      case 'surah':
        return <BookOpen className="w-4 h-4" />
      case 'video':
        return <Play className="w-4 h-4" />
      default:
        return <Search className="w-4 h-4" />
    }
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      if (document.body) {
        document.body.style.overflow = 'hidden'
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      if (document.body) {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed inset-x-4 top-20 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 max-w-2xl mx-auto max-h-[80vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={t('common.search')}
      >
        <div className="flex items-center border-b border-gray-200 dark:border-gray-700 p-4">
          <Search className="w-5 h-5 text-gray-400 dark:text-gray-500 rtl:mr-3 ltr:ml-3" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('common.search') + '...'}
            className="flex-1 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent"
            aria-label={t('common.search')}
          />
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              {t('common.loading')}
            </div>
          )}

          {!isLoading && searchQuery.length >= 2 && results.length === 0 && (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              لا توجد نتائج للبحث عن "{searchQuery}"
            </div>
          )}

          {!isLoading && searchQuery.length < 2 && (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              اكتب على الأقل حرفين للبحث
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-1">
              {results.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleResultClick(result)}
                  className="w-full text-right p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition-colors flex items-center gap-3"
                  aria-label={`${getLocalizedValue(result.title, currentLang)} - ${result.type}`}
                >
                  <div className="text-gray-400 dark:text-gray-500">{getIcon(result.type)}</div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{getLocalizedValue(result.title, currentLang)}</div>
                    {result.description && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{getLocalizedValue(result.description, currentLang)}</div>
                    )}
                    {result.category && (
                      <div className="text-xs text-primary-600 dark:text-primary-400 mt-1">{getLocalizedValue(result.category, currentLang)}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-500 dark:text-gray-400">
          اضغط ESC للإغلاق
        </div>
      </div>
    </>
  )
}

